/*
 * Copyright (c) 2021 - 2025, Ludvig Lundgren and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { Fragment } from "react";
import { UserIcon } from "@heroicons/react/24/solid";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";

import { classNames } from "@utils";
import { OpenIdIcon } from "@components/Icons";

import { RightNavProps } from "./_shared";
import { InboxMenu } from "./InboxMenu";

import { Cog6ToothIcon, ArrowLeftOnRectangleIcon, MoonIcon, SunIcon, ComputerDesktopIcon } from "@heroicons/react/24/outline";
import { Link } from "@tanstack/react-router";
import { AuthContext, SettingsContext, isDarkTheme } from "@utils/Context";
import type { Theme } from "@utils/Context";

export const RightNav = (props: RightNavProps) => {
  const { t } = useTranslation("common");
  const [settings, setSettings] = SettingsContext.use();
  const auth = AuthContext.get();

  const nextTheme: Record<Theme, Theme> = {
    light: "dark",
    dark: "system",
    system: "light"
  };

  const themeIcons: Record<Theme, typeof MoonIcon> = {
    dark: MoonIcon,
    light: SunIcon,
    system: ComputerDesktopIcon
  };

  const themeLabels: Record<Theme, string> = {
    light: t("theme.toggleLightToDark"),
    dark: t("theme.toggleDarkToSystem"),
    system: t("theme.toggleSystemToLight")
  };

  const toggleTheme = () => {
    setSettings(prevState => ({
      ...prevState,
      theme: nextTheme[prevState.theme]
    }));
  };

  const ThemeIcon = themeIcons[settings.theme];

  return (
    <div className="hidden sm:block">
      <div className="ml-4 flex items-center sm:ml-6">
        <div className="mr-2">
          <InboxMenu />
        </div>
        <div className="mt-1 items-center">
          <button
            onClick={toggleTheme}
            className="p-1 rounded-full hover:cursor-pointer focus:outline-hidden focus:none transition duration-100 ease-out transform hover:bg-gray-200 dark:hover:bg-gray-800 hover:scale-100"
            title={themeLabels[settings.theme]}
          >
            <ThemeIcon className={`h-4 w-4 transition duration-100 ease-out transform ${isDarkTheme(settings.theme) ? "text-gray-500" : "text-gray-600"}`} aria-hidden="true" />
          </button>
        </div>
        <Menu as="div" className="ml-2 relative">
          {({ open }) => (
            <>
              <MenuButton
                className={classNames(
                  "cursor-pointer",
                  open ? "bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white" : "hover:text-gray-900 dark:hover:text-white",
                  "text-gray-600 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-800 px-3 py-2 rounded-2xl text-sm font-medium",
                  "max-w-xs rounded-full flex items-center text-sm px-3 py-2",
                  "transition duration-200"
                )}
              >
                <span className="hidden hover:cursor-pointer text-sm font-medium sm:flex items-center">
                  <span className="sr-only">
                    Open user menu for{" "}
                  </span>
                  <span className="flex items-center">{auth.username}</span>
                  {auth.authMethod === 'oidc' ? (
                    auth.profilePicture ? (
                      <div className="relative flex-shrink-0 ml-2 w-6 h-6 overflow-hidden rounded-full ring-1 ring-white dark:ring-gray-700">
                        <img
                          src={auth.profilePicture}
                          alt={`${auth.username}'s profile`}
                          className="object-cover w-full h-full transition-opacity duration-200"
                          onError={() => auth.profilePicture = undefined}
                        />
                      </div>
                    ) : (
                      <OpenIdIcon className="inline ml-1 h-4 w-4 text-gray-500 dark:text-gray-500" />
                    )
                  ) : (
                    <UserIcon
                      className="inline ml-1 h-5 w-5"
                      aria-hidden="true"
                    />
                  )}
                </span>
              </MenuButton>
              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems
                  static
                  className="origin-top-right absolute right-0 mt-2 w-48 z-10 divide-y divide-gray-100 dark:divide-gray-750 rounded-md shadow-lg bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 focus:outline-hidden"
                >
                  <MenuItem>
                    {({ active }) => (
                      <Link
                        to="/settings/account"
                        className={classNames(
                          active
                            ? "bg-gray-100 dark:bg-gray-600"
                            : "",
                          "flex items-center transition rounded-t-md px-2 py-2 text-sm text-gray-900 dark:text-gray-200"
                        )}
                      >
                        <UserIcon
                          className="w-5 h-5 mr-1 text-gray-700 dark:text-gray-400"
                          aria-hidden="true"
                        />
                        {t("userMenu.account")}
                      </Link>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <Link
                        to="/settings"
                        className={classNames(
                          active
                            ? "bg-gray-100 dark:bg-gray-600"
                            : "",
                          "flex items-center transition px-2 py-2 text-sm text-gray-900 dark:text-gray-200"
                        )}
                      >
                        <Cog6ToothIcon
                          className="w-5 h-5 mr-1 text-gray-700 dark:text-gray-400"
                          aria-hidden="true"
                        />
                        {t("userMenu.settings")}
                      </Link>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          props.logoutMutation();
                        }}
                        className={classNames(
                          "cursor-pointer",
                          active
                            ? "bg-gray-100 dark:bg-gray-600"
                            : "",
                          "flex items-center transition rounded-b-md w-full px-2 py-2 text-sm text-gray-900 dark:text-gray-200 text-left"
                        )}
                      >
                        <ArrowLeftOnRectangleIcon
                          className="w-5 h-5 mr-1 text-gray-700 dark:text-gray-400"
                          aria-hidden="true"
                        />
                        {t("userMenu.logout")}
                      </button>
                    )}
                  </MenuItem>
                </MenuItems>
              </Transition>
            </>
          )}
        </Menu>
      </div>
    </div>
  );
};
